"use client";

import { useState, useEffect, useCallback } from "react";
import { askCohere } from "../apis/service/cohereService";
import {
  getAllCompanyObjectivesAcrossScorecards,
  getAllCompanyMeasuresAcrossScorecards,
} from "../apis/service/companyObjectiveService";

interface AnalysisInsight {
  type: "performance" | "trend" | "recommendation" | "alert";
  title: string;
  description: string;
  severity: "high" | "medium" | "low";
  data?: any;
}

export const useAssistant = (companyId: string) => {
  const [messages, setMessages] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState<AnalysisInsight[]>([]);
  const [autoAnalysis, setAutoAnalysis] = useState<string>("");
  const [cachedData, setCachedData] = useState<any>(null);

  // Cache data to avoid repeated API calls
  const fetchData = useCallback(async () => {
    if (!companyId || cachedData) return cachedData;

    try {
      const [objectives, measures] = await Promise.all([
        getAllCompanyObjectivesAcrossScorecards(companyId),
        getAllCompanyMeasuresAcrossScorecards(companyId),
      ]);

      const data = {
        objectives: objectives.slice(0, 15).map((o: any) => ({
          title: o.title || o.description,
          weight: o.weight,
          currentScore: o.currentScore,
          scorecardId: o.scorecardId,
          perspective: o.perspective,
        })),
        measures: measures.slice(0, 20).map((m: any) => ({
          description: m.description,
          q2Rating: m.q2FinalRating || 0,
          q4Rating: m.q4FinalRating || 0,
          target: m.annualTarget,
          actual: m.annualActual,
          scorecardId: m.scorecardId,
          perspective: m.perspective,
        })),
        summary: {
          totalObjectives: objectives.length,
          totalMeasures: measures.length,
          avgQ2Rating:
            measures.length > 0
              ? measures.reduce(
                  (sum: number, m: any) => sum + (m.q2FinalRating || 0),
                  0
                ) / measures.length
              : 0,
          avgQ4Rating:
            measures.length > 0
              ? measures.reduce(
                  (sum: number, m: any) => sum + (m.q4FinalRating || 0),
                  0
                ) / measures.length
              : 0,
          redMeasures: measures.filter((m: any) => (m.q2FinalRating || 0) <= 2)
            .length,
          greenMeasures: measures.filter(
            (m: any) => (m.q2FinalRating || 0) >= 4
          ).length,
        },
      };

      setCachedData(data);
      return data;
    } catch (error) {
      console.error("Error fetching data:", error);
      return null;
    }
  }, [companyId, cachedData]);

  const generateAutoAnalysis = async () => {
    if (!companyId) return;

    setLoading(true);
    try {
      const analysisData = await fetchData();
      if (!analysisData) {
        setAutoAnalysis("Unable to load performance data for analysis.");
        setLoading(false);
        return;
      }

      const prompt = `You are a senior business analyst providing executive dashboard insights for a company performance management system.

PERFORMANCE DATA:
${JSON.stringify(analysisData, null, 2)}

Please provide a comprehensive executive summary analysis covering:
1. Overall Performance Status (2-3 sentences)
2. Key Strengths (top 2-3 areas)  
3. Critical Areas for Improvement (top 2-3 concerns)
4. Strategic Recommendations (3-4 actionable items)
5. Performance Trends (Q2 vs Q4 comparison)

Format your response in clear, executive-friendly language with specific metrics and actionable insights.`;

      const response = await askCohere(prompt);
      setAutoAnalysis(response);

      // Generate specific insights
      await generateInsights(analysisData);
    } catch (error) {
      console.error("Auto-analysis error:", error);
      setAutoAnalysis(
        "Unable to generate analysis at this time. Please check your data connection and try again later."
      );
    }
    setLoading(false);
  };

  const generateInsights = async (data: any) => {
    const newInsights: AnalysisInsight[] = [];

    // Performance alerts
    if (data.summary.avgQ2Rating < 2.5) {
      newInsights.push({
        type: "alert",
        title: "Low Performance Alert",
        description: `Average Q2 rating is ${data.summary.avgQ2Rating.toFixed(
          1
        )}, indicating significant performance concerns.`,
        severity: "high",
      });
    }

    // Trend analysis
    const trendImprovement =
      data.summary.avgQ4Rating - data.summary.avgQ2Rating;
    if (trendImprovement > 0.5) {
      newInsights.push({
        type: "trend",
        title: "Positive Performance Trend",
        description: `Performance improved by ${trendImprovement.toFixed(
          1
        )} points from Q2 to Q4.`,
        severity: "low",
      });
    } else if (trendImprovement < -0.5) {
      newInsights.push({
        type: "trend",
        title: "Declining Performance Trend",
        description: `Performance declined by ${Math.abs(
          trendImprovement
        ).toFixed(1)} points from Q2 to Q4.`,
        severity: "high",
      });
    }

    // Red measures alert
    if (data.summary.redMeasures > data.summary.totalMeasures * 0.3) {
      newInsights.push({
        type: "alert",
        title: "High Red Measures",
        description: `${data.summary.redMeasures} measures (${(
          (data.summary.redMeasures / data.summary.totalMeasures) *
          100
        ).toFixed(0)}%) are performing poorly.`,
        severity: "high",
      });
    }

    // Data availability check
    if (data.summary.totalMeasures === 0) {
      newInsights.push({
        type: "alert",
        title: "No Performance Data",
        description:
          "No KPI data available for analysis. Please ensure performance data is properly configured.",
        severity: "high",
      });
    }

    setInsights(newInsights);
  };

  const sendQuestion = async (question: string) => {
    if (!companyId) return;

    setLoading(true);
    try {
      const contextData = await fetchData();
      if (!contextData) {
        setMessages((prev) => [
          ...prev,
          `❌ Error: Could not load performance data. Please try again.`,
        ]);
        setLoading(false);
        return;
      }

      const prompt = `You are an AI business analyst. Based on this company performance data:

${JSON.stringify(contextData, null, 2)}

User Question: ${question}

Provide a detailed, data-driven response with specific insights and recommendations based on the actual performance data provided. Keep your response concise but informative.`;

      const response = await askCohere(prompt);
      setMessages((prev) => [...prev, `🧑‍💼: ${question}`, `🤖: ${response}`]);
    } catch (error) {
      console.error("Question processing error:", error);
      setMessages((prev) => [
        ...prev,
        `❌ Error: Could not process your request. Please try again.`,
      ]);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (companyId) {
      generateAutoAnalysis();
    }
  }, [companyId]);

  return {
    messages,
    loading,
    sendQuestion,
    insights,
    autoAnalysis,
    refreshAnalysis: generateAutoAnalysis,
  };
};
